import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import basementWallCodeTemplate from "features/Model/Enclosure/Templates/basementWallCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";

import EmbodiedCarbonFramingGroupGrade, {
    EmbodiedCarbonCalculationsSummary,
    StandardEmbodiedLayerBlock,
} from "components/EmbodiedCarbonGrade";
import globalStyles from "components/globalStyles.module.scss";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const framingValidation = getValidation("csBasementWallFraming");
const spacingValidation = getValidation("csBasementWallSpacing");
const studsValidation = getValidation("csBasementWallStudsCornersInt");
const framingInsValidation = getValidation("csBasementWallFramingIns");
const extraInsValidation = getValidation("csBasementWallExtraIns");
const interiorValidation = getValidation("csBasementWallFinish");

const StandardBasementCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        // const basementWallCodeLib = useStateSelector(`user.codeLib.BasementWall.codes`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};
        const { basementWallInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};

        const basementWallCode = useStateSelector(`${drawerAccessor}.basementWallCode`) || {};
        const { id: framingId, value: framingValue } =
            useStateSelector(`${drawerAccessor}.basementWallCode.layers.framing`) || {};
        const { value: spacingValue } = useStateSelector(`${drawerAccessor}.basementWallCode.layers.spacing`) || {};
        const { value: studsValue } =
            useStateSelector(`${drawerAccessor}.basementWallCode.layers.studsCornerIntersection`) || {};
        const { value: framingInsValue } =
            useStateSelector(`${drawerAccessor}.basementWallCode.layers.insulationFramingLayer`) || {};
        const { value: extraInsValue } =
            useStateSelector(`${drawerAccessor}.basementWallCode.layers.extraInsulationLayer`) || {};
        const { value: interiorFinishValue } =
            useStateSelector(`${drawerAccessor}.basementWallCode.layers.interiorFinish`) || {};
        const basementWallLabel = useStateSelector(`${drawerAccessor}.basementWallCode.label`) || "";

        const basementWallValue = `${framingValue}${spacingValue}${studsValue}${framingInsValue}${extraInsValue}${interiorFinishValue}`;

        const basementWallLayers = useStateSelector(`${drawerAccessor}.basementWallCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.basementWallCode.layers`;

        const noFraming = parseFloat(framingId) === 0;

        const { codeRef: editingCodeRef = null, isLibCode = false } = basementWallInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("BasementWall", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];
        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        const handleFramingChange = useCallback(
            ({ id, value }) => {
                // Handle condition when framing is set to none (0)
                if (id === 0) {
                    change(
                        `${drawerAccessor}.basementWallCode.layers.spacing`,
                        getDefaultOption("csBasementWallSpacing")
                    );
                    change(
                        `${drawerAccessor}.basementWallCode.layers.studsCornerIntersection`,
                        getDefaultOption("csBasementWallStudsCornersInt")
                    );
                    change(
                        `${drawerAccessor}.basementWallCode.layers.insulationFramingLayer`,
                        getDefaultOption("csBasementWallFramingIns")
                    );
                }
            },
            [framingId, change]
        );

        useEffect(() => {
            const initialValues = {
                basementWallCode: isEmpty(basementWallInsType) ? basementWallCodeTemplate : basementWallInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = basementWallValue;
        }, [basementWallValue, basementWallLabel]);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const basementEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Basement Wall Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"} style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.basementWallCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={basementWallLabel.toString() === valueRef.current ? basementWallValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.basementWallCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={basementWallValue}
                    />
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <CodeLibraryDestination
                                    drawerAccessor={drawerAccessor}
                                    editingCodeRef={editingCodeRef}
                                    isLibCode={isLibCode}
                                    compact
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.basementWallCode.layers.framing`}
                            label="Framing"
                            placeholder="Choose Framing"
                            options={getOptions({ fieldKey: "csBasementWallFraming" })}
                            validate={framingValidation}
                            onChange={handleFramingChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderBottom: "none",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.basementWallCode.layers.spacing`}
                            label="Spacing"
                            placeholder="Choose Spacing"
                            options={getOptions({ fieldKey: "csBasementWallSpacing" })}
                            validate={spacingValidation}
                            disabled={noFraming}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.basementWallCode.layers.studsCornerIntersection`}
                            label="Studs at Corners and Intersections"
                            placeholder="Choose Studs at Corners and Intersections"
                            options={getOptions({ fieldKey: "csBasementWallStudsCornersInt" })}
                            validate={studsValidation}
                            disabled={noFraming}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={basementWallLayers}
                        componentType="basementWall"
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationFramingLayer"
                    layers={basementWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="basementWall"
                    editorDisabled={noFraming}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={basementEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.basementWallCode.layers.insulationFramingLayer`}
                        label="Insulation in Framing Layer"
                        placeholder="Choose Insulation in Framing Layer"
                        options={getOptions({ fieldKey: "csBasementWallFramingIns" })}
                        validate={framingInsValidation}
                        disabled={noFraming}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="extraInsulationLayer"
                    layers={basementWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="basementWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={basementEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.basementWallCode.layers.extraInsulationLayer`}
                        label="Extra Insulation Layer"
                        placeholder="Choose Extra Insulation Layer"
                        options={getOptions({ fieldKey: "csBasementWallExtraIns" })}
                        validate={extraInsValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interiorFinish"
                    layers={basementWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="basementWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...basementEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.basementWallCode.layers.interiorFinish`}
                        label="Interior Finish"
                        placeholder="Choose Interior Finish"
                        options={getOptions({ fieldKey: "csBasementWallFinish" })}
                        validate={interiorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={basementWallCode}
                    componentType={"basementWall"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.basementWallCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "basementWallInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            basementWallCode = {},
            addToLibrary = false,
            orgLibraryId = "",
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: "BasementWall",
            componentId,
            code: basementWallCode,
            inputName: "basementWallInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardBasementCode);
